import { CSSProperties, useEffect, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { ReactComponent as ChevronDownCircleIcon } from "shared/assets/Icons/down-arrow-filled.svg";
import { ReactComponent as PauseIcon } from "shared/assets/Icons/pause.svg";
import { ReactComponent as PlayIcon } from "shared/assets/Icons/play.svg";
import { formatDuration } from "shared/utils/formatDuration";
import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { useMobilePlatform } from "core/hooks/useMobilePlatform";
import { trackAudioPlayerInteraction, trackAudioPlayerSeek } from "core/monitoring/Events";
import { AudioPlayerResource } from "../../../enums/types";

export const SLIDE_ANIMATION_DURATION_MS = 300;

const CONTROLS_BASE_PADDING_PX = 24;

type AudioPlayerPopupViewProps = {
  resource: AudioPlayerResource;
  duration: number;
  playTime: number;
  isPlaying: boolean;
  error: boolean;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSeekMouseDown: () => void;
  onSeekMouseUp: () => void;
  onSeekChange: (position: number) => void;
  onClose: () => void;
  onDone: () => void;
};

export const AudioPlayerPopupView = ({
  resource,
  duration,
  playTime,
  isPlaying,
  error,
  onPlay,
  onPause,
  onStop,
  onSeekMouseDown,
  onSeekMouseUp,
  onSeekChange,
  onClose,
  onDone,
}: AudioPlayerPopupViewProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "audioPlayer" });
  const { insetPadding } = useMobilePlatform();
  const [isVisible, setIsVisible] = useState(false);

  const controlsInsetStyle: CSSProperties | undefined = insetPadding
    ? {
        paddingTop: `${insetPadding.top + CONTROLS_BASE_PADDING_PX}px`,
        paddingBottom: `${insetPadding.bottom + CONTROLS_BASE_PADDING_PX}px`,
      }
    : undefined;

  useEffect(() => {
    const showTimer = setTimeout(() => setIsVisible(true), 0);
    return () => clearTimeout(showTimer);
  }, []);

  const dismiss = (callback: () => void) => {
    setIsVisible(false);
    setTimeout(callback, SLIDE_ANIMATION_DURATION_MS);
  };

  const handleCollapse = () => {
    trackAudioPlayerInteraction("close");
    onPause();
    dismiss(onClose);
  };

  const handleDone = () => {
    trackAudioPlayerInteraction("done");
    onStop();
    dismiss(onDone);
  };

  const handleTogglePlay = () => {
    if (isPlaying) {
      trackAudioPlayerInteraction("pause");
      onPause();
      return;
    }
    trackAudioPlayerInteraction("play");
    onPlay();
  };

  const handleSeekEnd = () => {
    trackAudioPlayerSeek(playTime, duration);
    onSeekMouseUp();
  };

  return (
    <div
      data-testid="audio-player"
      className={classNames(
        "fixed left-0 top-0 z-MODAL h-screen w-full overflow-hidden bg-black text-white transition-transform ease-in-out",
        isVisible ? "translate-y-0" : "translate-y-full"
      )}
      style={{ transitionDuration: `${SLIDE_ANIMATION_DURATION_MS}ms` }}
    >
      <img src={resource.imageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-black/50" />

      <div data-testid="audio-player-controls" className="relative z-10 flex h-full flex-col justify-between p-6" style={controlsInsetStyle}>
        <div className="flex items-center justify-between">
          <button aria-label={t("collapse")} onClick={handleCollapse}>
            <ChevronDownCircleIcon className="text-white h-8 w-8" />
          </button>
          <button className="font-bold" aria-label={t("done")} onClick={handleDone}>
            {t("done")}
          </button>
        </div>

        <div className="space-y-4">
          <h2 className="text-xl font-bold !text-white">{resource.title}</h2>

          <input
            disabled={duration === 0}
            type="range"
            className="accent-white h-2 w-full"
            aria-label={t("timeSlider")}
            value={duration > 0 ? playTime / duration : 0}
            min={0}
            max={1}
            step="any"
            onMouseDown={onSeekMouseDown}
            onMouseUp={handleSeekEnd}
            onTouchStart={onSeekMouseDown}
            onTouchEnd={handleSeekEnd}
            onChange={(e) => onSeekChange(parseFloat(e.currentTarget.value))}
          />

          <div className="flex justify-between text-sm">
            <span aria-label={t("elapsedTime")}>{formatDuration(playTime)}</span>
            <span aria-label={t("duration")}>{formatDuration(duration)}</span>
          </div>

          <div className="flex justify-center pb-2 pt-4">
            <button
              disabled={duration === 0}
              className="flex h-14 w-32 items-center justify-center rounded-full bg-white text-black"
              aria-label={isPlaying ? t("pauseLabel") : t("playLabel")}
              onClick={handleTogglePlay}
            >
              {isPlaying ? <PauseIcon className="h-7 w-7" /> : <PlayIcon className="ml-1 h-7 w-7" />}
            </button>
          </div>
        </div>
      </div>
      {error && <ErrorAlert content={t("loadingError")} />}
    </div>
  );
};
